import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Waves, 
  Film, 
  TreePalm, 
  Wifi, 
  LayoutGrid, 
  ArrowUpFromLine,
  ShieldCheck,
  Thermometer,
  Flame,
  Bath,
  Home,
  Accessibility
} from 'lucide-react';

const FeaturesSection = () => {
  const { t } = useLanguage();

  const features = [
    { icon: Waves, title: t('feature_pool'), desc: t('feature_pool_desc') },
    { icon: Film, title: t('feature_cinema'), desc: t('feature_cinema_desc') },
    { icon: TreePalm, title: t('feature_garden'), desc: t('feature_garden_desc') },
    { icon: Wifi, title: t('feature_domotics'), desc: t('feature_domotics_desc') },
    { icon: LayoutGrid, title: t('feature_terrace'), desc: t('feature_terrace_desc') },
    { icon: ArrowUpFromLine, title: t('feature_height'), desc: t('feature_height_desc') },
    { icon: ShieldCheck, title: t('feature_security'), desc: t('feature_security_desc') },
    { icon: Thermometer, title: t('feature_heating'), desc: t('feature_heating_desc') },
    { icon: Flame, title: t('feature_fireplace'), desc: t('feature_fireplace_desc') },
    { icon: Bath, title: t('feature_bathroom'), desc: t('feature_bathroom_desc') },
    { icon: Home, title: t('feature_studio'), desc: t('feature_studio_desc') },
    { icon: Accessibility, title: t('feature_pmr'), desc: t('feature_pmr_desc') },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <section 
      className="py-24 md:py-32 bg-white"
      data-testid="features-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12 md:mb-16"
        >
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] font-['Playfair_Display'] mb-4"
            data-testid="features-title"
          >
            {t('features_title')}
          </h2>
          <p className="text-[#5C5C59] text-base md:text-lg max-w-3xl">
            {t('features_subtitle')}
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="feature-card p-6 bg-[#F7F7F5] group"
                data-testid={`feature-card-${index}`}
              >
                <Icon 
                  className="w-7 h-7 text-[#C5A059] mb-4 group-hover:scale-110 transition-transform duration-300" 
                  strokeWidth={1} 
                />
                <h3 className="text-base font-medium text-[#1A1A1A] mb-2 font-['Manrope']">
                  {feature.title}
                </h3>
                <p className="text-sm text-[#5C5C59] leading-relaxed">
                  {feature.desc}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
